import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useReactToPrint } from 'react-to-print';

// MUI
import { DownloadRounded, UndoRounded } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';

// common
import MuiDialog from 'common/components/MuiDialog';
import MuiLoading from 'common/components/MuiLoading';
import NotFoundBox from 'common/components/NotFoundBox';
import useService from 'common/hook/useService';
import useSchoolYearMonthFilter from 'common/hook/useSchoolYearMonthFilter';
import { buildResourceSearchStr } from 'common/utils/schoolYearMonths';
import SchemaForm from 'common/SchemaForm/index';

// screen
import BalanceTable from 'modules/map/components/balance-cost/BalanceTable';
import PdfBalanceCost from 'modules/map/components/balance-cost/pdf';
import {
  BalanceCostFilterModel,
  BalanceCostModel,
  DetailMenuBalanceOperationEnum,
} from 'modules/map/components/balance-cost/types';
import { A4_PRINT_SIZE, formatUTC } from 'modules/map/components/pdf-print-form/utils';
import { ClientModel } from 'modules/map/types';

interface Props {
  clientData: ClientModel;
  open: boolean;
  onClose: () => void;
}
const BalanceCostDialog = (props: Props) => {
  const { open, onClose, clientData } = props;
  const contentToPrint = useRef(null);
  const service = useService();

  // Tháng/tuần do khung thực đơn của năm học quyết định — hook tự chọn khung mới nhất khi tải xong.
  const [filter, setFilter] = useState<BalanceCostFilterModel>({
    state: DetailMenuBalanceOperationEnum.confirm,
    week: 1,
  });
  const { monthOptions, weekOptions, framesFailed } = useSchoolYearMonthFilter(filter, setFilter);

  // Năm lấy từ tháng đã chọn, không phải dayjs().year(): học kỳ 2 nằm ở năm dương lịch sau.
  // Thiếu tuần thì KHÔNG dựng searchStr: bỏ trống một đoạn sẽ làm `day` trượt vào chỗ của `week`.
  const searchStr = useMemo(
    () =>
      filter.week
        ? buildResourceSearchStr({
            monthKey: filter.month,
            week: filter.week,
            day: 0,
            state: filter.state,
          })
        : null,
    [filter.month, filter.state, filter.week]
  );

  const { data, isFetching } = useQuery({
    queryKey: [searchStr, 'nutrition-menu'],
    queryFn: async () => {
      if (!service || !searchStr) return null;
      const json = await service.ClientDetailMenuBalanceController_list({
        params: {
          orgId: clientData.operationCode,
          mode: 'real',
        },
        queries: {
          limit: 50,
          offset: 0,
          searchStr: searchStr,
        },
      });
      return (json?.[0] as unknown as BalanceCostModel) ?? null;
    },
    enabled: open && !!service && !!searchStr,
  });
  const days = data?.structuredData?.data?.items || [];

  const fromDate = formatUTC(days[0]?.datetime, 'L');
  const toDate = formatUTC(days[days.length - 1]?.datetime, 'L');

  const handlePrint = useReactToPrint({
    contentRef: contentToPrint,
    pageStyle: A4_PRINT_SIZE,
  });

  const renderTable = ({
    isPrintedTable = false,
    refProp = null,
  }: {
    isPrintedTable?: boolean;
    refProp?: React.Ref<HTMLDivElement>;
  }) => {
    const renderTableContent = () => {
      if (isFetching) return <MuiLoading />;
      if (!data) return <NotFoundBox />;
      if (!data.structuredData.metadata.isUsed) return <NotFoundBox />;

      if (!isPrintedTable) return <BalanceTable data={data} />;
      return <PdfBalanceCost data={data} />;
    };

    return (
      <Box
        sx={{
          overflow: isPrintedTable ? 'hidden' : undefined,
          height: isPrintedTable ? 0 : '100%',
        }}
      >
        <Box ref={refProp} className="flex flex-col items-center justify-center w-full h-full">
          {renderTableContent()}
        </Box>
      </Box>
    );
  };

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      title={'map.balanceMenu'}
      maxWidth="desktop"
      fullWidth
      titleAlign="center"
      disablePortal
      PaperProps={{
        sx: {
          margin: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      <Box className="p-2 !pb-0 tablet:p-4">
        <SchemaForm<BalanceCostFilterModel>
          formData={filter}
          onChange={setFilter}
          globalProps={{ noHelperText: true }}
          schema={{
            fields: ({ formProps: { isMobile, intl } }) => {
              return {
                blank2: {
                  mode: 'node',
                  hidden: !isMobile,
                  render: () => {
                    return (
                      <Box className="flex items-center justify-center h-full">
                        <Typography variant="subtitle1">
                          {[fromDate, toDate].join(' - ')}
                        </Typography>
                      </Box>
                    );
                  },
                  propsWrapper: { mobile: 12 },
                },
                monthText: {
                  mode: 'node',
                  hidden: isMobile,
                  render: () => {
                    return (
                      <Typography mt={1}>
                        <FormattedMessage id="map.month" />
                      </Typography>
                    );
                  },
                  propsWrapper: { mobile: undefined },
                },
                month: {
                  mode: 'select',
                  label: isMobile && <FormattedMessage id="map.month" />,
                  options: monthOptions,
                  rawOptions: true,
                  disableClearBtn: true,
                  noHelperText: true,
                  propsWrapper: {
                    mobile: 6,
                    tablet: 2,
                    laptop: undefined,
                  },
                },
                weekText: {
                  mode: 'node',
                  hidden: isMobile,
                  render: () => {
                    return (
                      <Box>
                        <Typography mt={1}>
                          <FormattedMessage id="map.week" />
                        </Typography>
                        {weekOptions.length === 0 && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            {intl.formatMessage({
                              id: framesFailed ? 'map.weekLoadFailed' : 'map.noWeek',
                            })}
                          </Typography>
                        )}
                      </Box>
                    );
                  },
                  propsWrapper: { mobile: undefined },
                },
                // Tuần phụ thuộc tháng nên đứng SAU tháng và khoá lại khi tháng chưa chốt khung.
                week: {
                  mode: 'select',
                  label: isMobile && <FormattedMessage id="map.week" />,
                  options: weekOptions.map((w) => ({ id: w.id, name: w.id })),
                  disabled: weekOptions.length === 0,
                  rawOptions: true,
                  disableClearBtn: true,
                  noHelperText: true,
                  propsWrapper: { mobile: 6, tablet: 2 },
                },

                blank: {
                  mode: 'node',
                  hidden: isMobile,
                  render: () => {
                    return (
                      <Box className="flex items-center justify-center h-full">
                        <Typography>{data && [fromDate, toDate].join(' - ')}</Typography>
                      </Box>
                    );
                  },
                  propsWrapper: { mobile: true },
                },
                statusText: {
                  mode: 'node',
                  render: () => {
                    return (
                      <Typography mt={1} variant="subtitle1">
                        <FormattedMessage id="map.process" />
                        :&nbsp;
                      </Typography>
                    );
                  },
                  propsWrapper: { mobile: undefined },
                },
                state: {
                  label: isMobile && <FormattedMessage id="map.process" />,
                  mode: 'select',
                  options: [
                    {
                      id: DetailMenuBalanceOperationEnum.confirm,
                      name: intl.formatMessage({
                        id: 'map.confirm',
                      }),
                    },
                    {
                      id: DetailMenuBalanceOperationEnum.complete,
                      name: intl.formatMessage({
                        id: 'map.completed',
                      }),
                    },
                  ],
                  rawOptions: true,
                  disableClearBtn: true,
                  noHelperText: true,
                  propsWrapper: {
                    mobile: 6,
                    tablet: 2,
                    laptop: undefined,
                  },
                },
              };
            },
          }}
        />
      </Box>
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {renderTable({ isPrintedTable: true, refProp: contentToPrint })}
        {renderTable({})}
      </Box>
      <Box className="p-2 tablet:p-4 flex justify-end gap-2 tablet:gap-4">
        <Button
          startIcon={<DownloadRounded />}
          onClick={() => {
            handlePrint();
          }}
          disabled={!data}
        >
          <FormattedMessage id="map.download" />
        </Button>
        <Button variant="outlined" startIcon={<UndoRounded />} onClick={onClose}>
          <FormattedMessage id="back" />
        </Button>
      </Box>
    </MuiDialog>
  );
};
export default BalanceCostDialog;
